/* eslint-disable camelcase */
import * as React from "react"
import { FC, useCallback, useEffect, useState } from "react"
import { observer } from "mobx-react-lite"
import { FastField, Formik, FieldArray } from "formik"
import { FormControl, FormControlProps } from "../../form-control/form-control"
import { Appearance, TextInput, TextStyle, ViewStyle } from "react-native"
import { FormProps } from "../form"
import * as yup from "yup"
import { translate, TxKeyPath } from "../../../i18n"
import { NumeralInput, NumeralInputProps } from "../../input/numeral-input/numeral-input"
import { View } from "native-base"
import { color } from "../../../theme"
import { Checkbox } from "../../checkbox/checkbox"

const CHECKBOX: ViewStyle = {
  alignItems: 'center'
}

const FORM_CONTROL: ViewStyle = {
  width: '90%',
}

const CHECKBOX_OUTLINE: ViewStyle = {
  borderColor: color.palette.gray800,
  borderWidth: 2
}

const CHECKBOX_LABEL: TextStyle = {
  color: color.palette.gray800,
  marginLeft: 12
}

const TEXT_FIELD: ViewStyle = {
  alignItems: 'center',
  justifyContent: 'center',
  flex: 1,
  marginVertical: 0,
  paddingVertical: 0,
  minHeight: 45,
  borderRadius: 5,
  // width: "30%"
}

export interface CycleToDoSettingsFormProps extends FormProps {
  initialValues?: Array<any>,
  activeStatus?: Array<any>,
  onCheckParameter?: (key?: any) => () => void,
  isLoading?: boolean
}

const NumeralField = (
  { name, colorScheme = Appearance.getColorScheme(), formControlProps, numeralInputProps, index, input, labelTx }:
  { name: string, index?: number, colorScheme?: "light" | "dark", input?: any, labelTx?: string, formControlProps?: FormControlProps, numeralInputProps?: NumeralInputProps },
) => (
  <FastField name={name}>
    {({ field, form: { setFieldValue, handleBlur }, meta }) => (
      <FormControl
        isRequired
        isInvalid={(meta.initialValue || meta.touched) && !!meta.error}
        labelTx={labelTx as TxKeyPath}
        labelPreset="body2"
        labelColor={color.palette.gray800}
        colorScheme={colorScheme}
        inputProps={{
          testID: `jala:id/${field.name}-input`,
          autoCapitalize: "none",
          autoComplete: "off",
          autoCorrect: false,
          textContentType: "none",
          keyboardType: "numeric",
          value: field.value,
          onBlur: handleBlur(field.name),
          color: color.palette.gray800,
        }}
        errorMessage={meta.error}
        style={FORM_CONTROL}
        {...formControlProps}
      >
        {(inputProps) => (
          <NumeralInput
            ref={input[field.name]}
            colorScheme={colorScheme}
            style={TEXT_FIELD}
            onChangeNumber={(value) => setFieldValue(field.name, value)}
            format="0"
            {...inputProps}
            {...numeralInputProps}
          />
        )}
      </FormControl>
    )}
  </FastField>
)

const ParameterItem: FC<{
  colorScheme?: "light"|"dark",
  value?: any,
  onCheck?: () => void,
  title?: string,
  statusKey?: string,
  onChange?: (text: any, key: string) => void,
  inputValue?: any,
  index?: number,
  item?: any,
  input?: any,
  isLoading?: boolean
}> = observer(function ParameterItem(props) {
  const { colorScheme, value, onCheck, title, statusKey, index, input, isLoading } = props
  const [ isChecked, setIsChecked ] = useState<boolean>(false)

  useEffect(() => {
    if (value) {
      const isExist = !!value.find((i) => i === statusKey)
      setIsChecked(isExist)
    }
  }, [value, statusKey])

  return (
    <View paddingX="16px" marginTop="16px">
      <Checkbox
        value={isChecked}
        onToggle={onCheck}
        text={title}
        colorScheme={colorScheme}
        labelPreset="body2"
        labelStyle={CHECKBOX_LABEL}
        outlineStyle={CHECKBOX_OUTLINE}
        style={CHECKBOX}
        disabled={isLoading}
      />
      {isChecked &&
      <View  marginTop="14px">
        {/* <Text tx="cycleToDoScreen.inputDoc" preset="body2" color={color.palette.gray800} /> */}
        <View flexDirection="row" justifyContent="space-between">
          <View flex={1}>
            <NumeralField
              name={`cycle_to_do[${index}].from`}
              colorScheme={colorScheme}
              index={index}
              input={input}
              labelTx="cycleToDoScreen.rangeDocFrom"
              formControlProps={{ isRequired: true }}
            />
          </View>
          <View flex={1}>
            <NumeralField
              name={`cycle_to_do[${index}].to`}
              colorScheme={colorScheme}
              index={index}
              input={input}
              labelTx="cycleToDoScreen.rangeDocTo"
              formControlProps={{ isRequired: true }}
            />
          </View>
        </View>
      </View>
      }
    </View>
  )
})

/**
 * Describe your component here
 */
export const CycleToDoSettingsForm = observer(function CycleToDoSettingsForm(props: CycleToDoSettingsFormProps) {
  const { initialValues = [], activeStatus=[],  onSubmit: onSubmitProps, colorScheme = Appearance.getColorScheme(), innerRef, onCheckParameter, isLoading, ...rest } = props

  const input = {
    from: React.useRef<TextInput>(),
    to: React.useRef<TextInput>()
  }

  const validationSchema = yup.object({
    cycle_to_do: yup.array().of(
      yup.object().shape({
        from: yup.number().nullable().required().label(translate("cycleToDoScreen.rangeDoc"))
          .moreThan(-1, translate("cantBeMinus"))
          .lessThan(130, translate("cycleToDoScreen.lessThan130", {field: translate("cycleToDoScreen.rangeDocFrom")})),
        to: yup.number().nullable().required().label(translate("cycleToDoScreen.rangeDoc"))
          .moreThan(0, translate("cantBeZero"))
          .moreThan(yup.ref("from"))
          .lessThan(130, translate("cycleToDoScreen.lessThan130", {field: translate("cycleToDoScreen.rangeDocTo")}))
      })
    )
    .required()
  });

  const onSubmit = useCallback((values) => {
    const data = { cycle_to_do: values.cycle_to_do, activeStatus }

    onSubmitProps(data)
  }, [activeStatus, onSubmitProps])

  return (
    <Formik
      initialValues={{ cycle_to_do: initialValues }}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ values }) => (
        <>
          <FieldArray name="cycle_to_do"
            render={() => (
              <>
                {values.cycle_to_do.map((item, index) => {
                  return (
                    <ParameterItem
                      key={index}
                      colorScheme={colorScheme}
                      title={translate(`cycleToDoScreen.${item.key}` as TxKeyPath)}
                      onCheck={onCheckParameter(item.key)}
                      value={activeStatus}
                      statusKey={item.key}
                      item={item}
                      index={index}
                      input={input}
                      isLoading={isLoading}
                    />
                  )
                })}
              </>
            )}
          />
        </>
      )}
    </Formik>
  )
})

CycleToDoSettingsForm.displayName = 'CycleToDoSettingsForm'
